import { useEffect, useMemo, useState } from 'react';
import { centsToPesos, PAYMENT_METHODS, STOCK_UNITS, PURCHASE_STATUS_LABELS, type PurchaseStatus } from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Field, Modal, Pager, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';
import type { InventoryItemDto } from './InventoryPage';

/**
 * Phase 2E — Purchasing.
 *
 * Purchase list (branch scope, status/search/date filters), a multi-line
 * creation form with live totals preview (the server recomputes every
 * total authoritatively), and a detail view carrying the lifecycle actions
 * (post → stock-in, controlled cancel) plus stock-in traceability rows.
 */

export interface PurchaseItemDto {
  id: string;
  inventoryItemId: string | null;
  inventoryItemName: string | null;
  description: string;
  unit: string;
  quantity: number;
  unitPriceCents: number;
  lineCents: number;
}

export interface PurchaseDto {
  id: string;
  number: string;
  branch: { id: string; code: string };
  purchaseDate: string;
  supplier: string;
  status: PurchaseStatus;
  totalCents: number;
  paymentMethod: string | null;
  receiptNumber: string | null;
  notes: string | null;
  items: PurchaseItemDto[];
  createdBy: { name: string } | null;
  postedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  updatedAt: string;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Page size the list asks the server for (the server total drives the count). */
const PAGE_SIZE = 50;

/**
 * Calendar-date display: purchaseDate is stored as UTC midnight of the
 * entered date, so format WITH time-zone:UTC — local-time conversion
 * shifts it back a day for browsers west of UTC.
 */
function formatDateOnly(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PH', { dateStyle: 'medium', timeZone: 'UTC' });
}

function statusTone(status: PurchaseStatus): 'slate' | 'emerald' | 'rose' {
  if (status === 'POSTED') return 'emerald';
  if (status === 'CANCELLED') return 'rose';
  return 'slate';
}

/** Today as the USER's local calendar date (not UTC - same evening/morning). */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* ---------------- create form ---------------- */

interface LineState {
  key: number;
  inventoryItemId: string;
  description: string;
  unit: string;
  quantity: string;
  unitPrice: string; // pesos, display only
}

let lineKey = 1;

function emptyLine(): LineState {
  return { key: lineKey++, inventoryItemId: '', description: '', unit: 'bottles', quantity: '1', unitPrice: '' };
}

function CreatePurchaseModal({
  branchId,
  inventoryItems,
  onClose,
  onCreated,
}: {
  branchId: string;
  inventoryItems: InventoryItemDto[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [purchaseDate, setPurchaseDate] = useState(todayIso());
  const [supplier, setSupplier] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineState[]>([emptyLine()]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const updateLine = (key: number, patch: Partial<LineState>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const lineTotal = (l: LineState) => {
    const qty = Number(l.quantity);
    const price = Number(l.unitPrice);
    if (!Number.isFinite(qty) || !Number.isFinite(price) || qty <= 0 || price < 0) return 0;
    return Math.round(qty * Math.round(price * 100));
  };
  const previewTotal = useMemo(() => lines.reduce((sum, l) => sum + lineTotal(l), 0), [lines]);

  const pickInventoryItem = (key: number, itemId: string) => {
    const item = inventoryItems.find((i) => i.id === itemId);
    if (!item) {
      updateLine(key, { inventoryItemId: '' });
      return;
    }
    updateLine(key, {
      inventoryItemId: item.id,
      description: item.name,
      unit: item.unit,
    });
  };

  const submit = async () => {
    setError(null);
    const payloadLines = lines
      .filter((l) => l.description.trim() && Number(l.quantity) > 0 && l.unitPrice !== '')
      .map((l) => ({
        inventoryItemId: l.inventoryItemId || undefined,
        description: l.description.trim(),
        unit: l.unit,
        quantity: Number(l.quantity),
        unitPriceCents: Math.round(Number(l.unitPrice) * 100),
      }));
    if (payloadLines.length === 0) {
      setError('Add at least one line with description, quantity and unit price.');
      return;
    }
    if (payloadLines.some((l) => !Number.isInteger(l.quantity) || l.quantity < 1)) {
      setError('Quantities must be whole numbers of 1 or more.');
      return;
    }
    if (payloadLines.some((l) => !Number.isInteger(l.unitPriceCents) || l.unitPriceCents < 0)) {
      setError('Unit prices must be valid amounts.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/purchases', {
        branchId,
        purchaseDate,
        supplier: supplier.trim(),
        paymentMethod: paymentMethod || undefined,
        receiptNumber: receiptNumber.trim() || undefined,
        notes: notes.trim() || undefined,
        items: payloadLines,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create the purchase.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="New purchase"
      onClose={onClose}
      footer={
        <>
          <span className="mr-auto text-sm text-slate-500">
            Total preview{' '}
            <span className="font-semibold text-slate-800">{centsToPesos(previewTotal)}</span>
          </span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={saving || !supplier.trim()}>
            {saving ? 'Saving…' : 'Save as draft'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorBanner message={error} />}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Purchase date" htmlFor="pur-date">
            <input
              id="pur-date"
              type="date"
              className={inputCls}
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
            />
          </Field>
          <Field label="Supplier" htmlFor="pur-supplier">
            <input
              id="pur-supplier"
              className={inputCls}
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder="e.g. ABC Supply"
            />
          </Field>
          <Field label="Payment method" htmlFor="pur-pay">
            <select
              id="pur-pay"
              className={inputCls}
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="">—</option>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Receipt / reference no." htmlFor="pur-receipt">
            <input
              id="pur-receipt"
              className={inputCls}
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value)}
            />
          </Field>
        </div>

        <div className="space-y-3">
          <p className="text-sm font-semibold text-slate-700">Items</p>
          {lines.map((l) => (
            <div key={l.key} className="rounded-xl border border-slate-200 p-3">
              <div className="grid grid-cols-4 gap-2">
                <select
                  aria-label="Inventory item"
                  className={`${inputCls} col-span-2 h-11`}
                  value={l.inventoryItemId}
                  onChange={(e) => pickInventoryItem(l.key, e.target.value)}
                >
                  <option value="">Custom item…</option>
                  {inventoryItems.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name} ({i.branchCode})
                    </option>
                  ))}
                </select>
                <input
                  aria-label="Description"
                  className={`${inputCls} col-span-1 h-11`}
                  placeholder="Description"
                  value={l.description}
                  onChange={(e) => updateLine(l.key, { description: e.target.value })}
                />
                <input
                  aria-label="Quantity"
                  type="number"
                  min={1}
                  className={`${inputCls} h-11`}
                  placeholder="Qty"
                  value={l.quantity}
                  onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                />
                <input
                  aria-label="Unit price"
                  type="number"
                  min={0}
                  step="0.01"
                  className={`${inputCls} h-11`}
                  placeholder="Unit price ₱"
                  value={l.unitPrice}
                  onChange={(e) => updateLine(l.key, { unitPrice: e.target.value })}
                />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <select
                  aria-label="Unit"
                  className={`${inputCls} h-9 w-auto text-sm`}
                  value={l.unit}
                  onChange={(e) => updateLine(l.key, { unit: e.target.value })}
                >
                  {STOCK_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-slate-600">
                  Line total{' '}
                  <span className="font-semibold text-slate-800">{centsToPesos(lineTotal(l))}</span>
                </span>
                {lines.length > 1 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => setLines((ls) => [...ls, emptyLine()])}>
            + Add line
          </Button>
        </div>

        <Field label="Notes (optional)" htmlFor="pur-notes">
          <textarea
            id="pur-notes"
            className={`${inputCls} h-16 py-2`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/* ---------------- detail ---------------- */

function DetailModal({
  purchase,
  onClose,
  onChanged,
}: {
  purchase: PurchaseDto;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // One deliberate in-app confirmation step (same convention as the
  // Phase 2D release modal) - never a native browser dialog.
  const [confirming, setConfirming] = useState<'post' | 'cancel' | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const act = async (action: 'post' | 'cancel') => {
    setError(null);
    setBusy(true);
    try {
      await api.post(
        `/purchases/${purchase.id}/${action}`,
        action === 'cancel'
          ? { reason: cancelReason.trim() || 'Cancelled from detail view' }
          : undefined,
      );
      setConfirming(null);
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Action failed.');
      setConfirming(null);
    } finally {
      setBusy(false);
    }
  };

  const confirmCopy =
    confirming === 'post'
      ? {
          title: 'Post this purchase?',
          body: `Posting ${purchase.number} records one STOCK_IN movement per linked item, referencing this purchase number. The lines freeze afterwards.`,
          cta: 'Yes, post it',
        }
      : {
          title:
            purchase.status === 'POSTED'
              ? 'Cancel this posted purchase and reverse its stock?'
              : 'Cancel this purchase?',
          body:
            purchase.status === 'POSTED'
              ? `${purchase.number} is already posted, so cancelling writes compensating STOCK_OUT movements that reverse its stock-in. The reversal needs enough stock on hand — if the stock was already used, the cancel is refused and nothing changes. The document is recorded as cancelled, never deleted.`
              : `Cancelling ${purchase.number} is recorded, never deleted. Nothing hit inventory, so no stock is moved.`,
          cta: purchase.status === 'POSTED' ? 'Yes, cancel and reverse' : 'Yes, cancel it',
        };

  return (
    <Modal
      title={purchase.number}
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Close
          </Button>
          {purchase.status === 'DRAFT' && !confirming && (
            <>
              <Button variant="danger" onClick={() => setConfirming('cancel')} disabled={busy}>
                Cancel purchase
              </Button>
              <Button onClick={() => setConfirming('post')} disabled={busy}>
                Post (stock in)
              </Button>
            </>
          )}
          {purchase.status === 'POSTED' && !confirming && (
            <Button variant="danger" onClick={() => setConfirming('cancel')} disabled={busy}>
              Cancel &amp; reverse stock
            </Button>
          )}
          {confirming && (
            <>
              <Button variant="secondary" onClick={() => setConfirming(null)} disabled={busy}>
                Back
              </Button>
              <Button
                variant={confirming === 'cancel' ? 'danger' : 'primary'}
                onClick={() => void act(confirming)}
                disabled={busy}
              >
                {busy ? 'Working…' : confirmCopy.cta}
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorBanner message={error} />}
        {confirming && (
          <Card className="border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900">{confirmCopy.title}</p>
            <p className="mt-1 text-sm text-amber-800">{confirmCopy.body}</p>
            {confirming === 'cancel' && (
              <div className="mt-3">
                <Field label="Reason (optional)" htmlFor="purchase-cancel-reason">
                  <textarea
                    id="purchase-cancel-reason"
                    className={`${inputCls} h-16 py-2`}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Recorded in the audit trail"
                  />
                </Field>
              </div>
            )}
          </Card>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={statusTone(purchase.status)}>{PURCHASE_STATUS_LABELS[purchase.status]}</Badge>
          <Badge tone="indigo">{purchase.branch.code}</Badge>
          <span className="text-sm text-slate-500">
            {formatDateOnly(purchase.purchaseDate)} ·{' '}
            {purchase.supplier}
          </span>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div>
            <dt className="text-slate-500">Payment</dt>
            <dd className="font-medium text-slate-800">{purchase.paymentMethod ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Receipt ref</dt>
            <dd className="font-medium text-slate-800">{purchase.receiptNumber ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Recorded by</dt>
            <dd className="font-medium text-slate-800">{purchase.createdBy?.name ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Posted</dt>
            <dd className="font-medium text-slate-800">
              {purchase.postedAt ? formatWhen(purchase.postedAt) : '—'}
            </dd>
          </div>
        </dl>

        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Unit price</th>
                <th className="px-3 py-2 text-right">Line total</th>
                <th className="px-3 py-2">Stock link</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {purchase.items.map((i) => (
                <tr key={i.id}>
                  <td className="px-3 py-2 font-medium text-slate-800">{i.description}</td>
                  <td className="px-3 py-2 text-right">
                    {i.quantity} {i.unit}
                  </td>
                  <td className="px-3 py-2 text-right">{centsToPesos(i.unitPriceCents)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{centsToPesos(i.lineCents)}</td>
                  <td className="px-3 py-2">
                    {i.inventoryItemId ? (
                      <Badge tone="emerald">{i.inventoryItemName ?? 'linked'}</Badge>
                    ) : (
                      <Badge tone="slate">not linked</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 bg-slate-50">
                <td colSpan={3} className="px-3 py-2 text-right text-xs font-semibold uppercase text-slate-500">
                  Purchase total
                </td>
                <td className="px-3 py-2 text-right text-base font-bold text-slate-900">
                  {centsToPesos(purchase.totalCents)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        {purchase.status === 'POSTED' && (
          <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            <Icon name="check" className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Posted — each linked line created a STOCK_IN movement referencing{' '}
              <span className="font-semibold">{purchase.number}</span>. Trace it in Inventory → History.
            </span>
          </div>
        )}
        {purchase.status === 'CANCELLED' && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            Cancelled{purchase.cancelledAt ? ` on ${formatWhen(purchase.cancelledAt)}` : ''}
            {purchase.cancelReason ? ` — ${purchase.cancelReason}` : ''}. The document and its
            movements are preserved, never deleted.
          </div>
        )}
        {purchase.notes && <p className="text-sm text-slate-600">{purchase.notes}</p>}
      </div>
    </Modal>
  );
}

/* ---------------- main page ---------------- */

export function PurchasingPage() {
  const { branchFilter } = useAuth();
  const [items, setItems] = useState<PurchaseDto[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');

  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [inventoryItems, setInventoryItems] = useState<InventoryItemDto[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  // A new filter means a new result set: always start from the first page.
  useEffect(() => {
    setOffset(0);
  }, [branchFilter, query, status]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (query) params.set('q', query);
    if (status) params.set('status', status);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));

    void api
      .get<{ total: number; items: PurchaseDto[] }>(`/purchases?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setItems(res.items);
          setTotal(res.total);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load purchases.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, query, status, offset, refreshKey]);

  // Inventory items for the create form (single fetch, branch-scoped server-side).
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    void api
      .get<{ items: InventoryItemDto[] }>(`/inventory?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setInventoryItems(res.items);
      })
      .catch(() => {
        /* create form still works with custom lines */
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);
  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;
  const detail = items.find((p) => p.id === detailId) ?? null;

  // The create form must post to a concrete branch - resolve the filter.
  const { branches, user } = useAuth();
  const createBranchId =
    branchFilter !== 'ALL'
      ? (branches.find((b) => b.code === branchFilter)?.id ?? '')
      : (user?.branchId ?? branches[0]?.id ?? '');

  return (
    <section className="space-y-4" aria-label="Purchasing">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Purchasing</h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · purchases are their own ledger — posting stocks inventory, never expenses
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={refresh}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
          <Button
            onClick={() => {
              if (branchFilter === 'ALL' && !createBranchId) {
                setError('Select a branch (LH1 or LH2) before creating a purchase.');
                return;
              }
              setCreating(true);
            }}
          >
            New purchase
          </Button>
        </div>
      </div>

      <Card className="flex flex-wrap items-center gap-3 p-4">
        <input
          aria-label="Search purchases"
          className={`${inputCls} h-11 max-w-xs flex-1`}
          placeholder="Search number, supplier, receipt…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          aria-label="Filter by status"
          className={`${inputCls} h-11 w-auto`}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {(['DRAFT', 'POSTED', 'CANCELLED'] as const).map((s) => (
            <option key={s} value={s}>
              {PURCHASE_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </Card>

      {error && <ErrorBanner message={error} />}

      <Card className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner className="h-6 w-6 text-indigo-600" />
          </div>
        ) : total === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm font-medium text-slate-700">No purchases match.</p>
            <p className="mt-1 text-xs text-slate-500">
              Record supplies with <span className="font-semibold">New purchase</span>, then post to
              stock inventory.
            </p>
          </div>
        ) : (
          <>
            <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Number</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Supplier</th>
                <th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{p.number}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDateOnly(p.purchaseDate)}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{p.supplier}</td>
                  <td className="px-4 py-3">
                    <Badge tone="indigo">{p.branch.code}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">
                    {centsToPesos(p.totalCents)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={statusTone(p.status)}>{PURCHASE_STATUS_LABELS[p.status]}</Badge>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setDetailId(p.id)}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
            <Pager
              total={total}
              offset={offset}
              loaded={items.length}
              onPrev={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              onNext={() => setOffset(offset + PAGE_SIZE)}
            />
          </>
        )}
      </Card>

      {creating && (
        <CreatePurchaseModal
          branchId={createBranchId}
          inventoryItems={inventoryItems}
          onClose={() => setCreating(false)}
          onCreated={refresh}
        />
      )}
      {detail && (
        <DetailModal purchase={detail} onClose={() => setDetailId(null)} onChanged={refresh} />
      )}
    </section>
  );
}
